import { ScrollText, AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// EstateOverviewPanel — shared component used by both the client portal and
// the admin Matter Detail. Reads `matter.calculator_metadata` (populated when
// a deceased_estate matter is created from the public calculator) and
// `matter.rescue_tracker` (when a rescue path has been selected). Stays
// silent when there is no metadata or the matter is not a deceased_estate.

const COMPLEXITY_STYLES = {
  simple:   "bg-emerald-50 text-emerald-700",
  moderate: "bg-amber-50  text-amber-700",
  complex:  "bg-red-50    text-red-700",
};

const STATUS_STYLES = {
  pending:     { bg: "bg-zinc-100",    text: "text-zinc-600",    icon: "○" },
  in_progress: { bg: "bg-blue-50",     text: "text-blue-700",    icon: "◐" },
  complete:    { bg: "bg-emerald-50",  text: "text-emerald-700", icon: "●" },
  blocked:     { bg: "bg-red-50",      text: "text-red-700",     icon: "✕" },
};

export default function EstateOverviewPanel({ matter, role = "client", onChanged }) {
  const meta = matter?.calculator_metadata;
  const matterType = matter?.matter_type || matter?.service_type;
  if (!meta || matterType !== "deceased_estate" || meta.source !== "deceased_estate_calculator") {
    return null;
  }

  const tracker = matter?.rescue_tracker;        // admin-only (full)
  const rescueSummary = matter?.rescue_summary;  // client-side sanitized

  const liquidityLevel = meta.liquidity_level;
  const liquidityStyles = {
    sufficient: { bg: "bg-emerald-50", border: "border-emerald-300", icon: <CheckCircle2 className="h-5 w-5 text-emerald-700" />, label: "Estate can cover administration costs" },
    borderline: { bg: "bg-amber-50",    border: "border-amber-300",   icon: <AlertTriangle className="h-5 w-5 text-amber-700" />,  label: "Cash is tight — your attorney will assist" },
    shortfall:  { bg: "bg-red-50",      border: "border-red-300",     icon: <AlertTriangle className="h-5 w-5 text-red-700" />,    label: "Your estate has a potential shortfall — your attorney will assist you with solutions" },
  }[liquidityLevel] || {};

  const fmt = (v) => (typeof v === "number" ? `R ${v.toLocaleString("en-ZA")}` : "—");
  const hasShortfall = liquidityLevel === "shortfall" || liquidityLevel === "borderline";

  return (
    <div className="border border-zinc-200 bg-white" data-testid="estate-overview-panel">
      <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-2">
        <ScrollText className="h-4 w-4 text-amber-700" />
        <div>
          <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Estate Overview</div>
          <div className="text-sm text-zinc-900 font-medium mt-0.5">From your initial calculator estimate</div>
        </div>
      </div>

      {/* Headline grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-zinc-200" data-testid="estate-overview-grid">
        <Cell label="Gross estate"        value={fmt(meta.gross_estate)}     testid="overview-gross-estate" />
        <Cell label="Executor's fees"     value={fmt(meta.executor_fees)}    testid="overview-executor-fees" />
        <Cell label="Estate duty"         value={fmt(meta.estate_duty)}      testid="overview-estate-duty" />
        <Cell label="Estimated total cost" value={meta.total_cost_label || fmt(meta.total_cost_mid)} testid="overview-total-cost" />
      </div>

      {/* Liquidity strip */}
      {liquidityLevel && (
        <div className={`px-6 py-4 border-t-2 ${liquidityStyles.border} ${liquidityStyles.bg} flex items-start gap-3`}
             data-testid={`estate-overview-liquidity-${liquidityLevel}`}>
          {liquidityStyles.icon}
          <div className="flex-1">
            <div className="text-sm font-medium text-zinc-900" data-testid="estate-overview-liquidity-label">
              {liquidityStyles.label || meta.liquidity_label}
            </div>
            {hasShortfall && (
              <div className="text-xs text-zinc-700 mt-1">
                {role === "admin" && meta.shortfall_high > 0 && (
                  <span>Potential shortfall up to <strong>R {meta.shortfall_high.toLocaleString("en-ZA")}</strong>. </span>
                )}
                {meta.rescue_options?.length > 0 && !tracker && !rescueSummary?.active && (
                  <span>Discussion options: {meta.rescue_options.map((o) => o.title).join(" · ")}.</span>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rescue tracker — client-side friendly summary */}
      {role !== "admin" && rescueSummary?.active && (
        <ClientRescueNotice summary={rescueSummary} />
      )}

      {/* Admin rescue tracker — full workflow */}
      {role === "admin" && hasShortfall && (
        <AdminRescueSection
          matter={matter}
          tracker={tracker}
          rescueOptions={meta.rescue_options || []}
          onChanged={onChanged}
        />
      )}

      <div className="px-6 py-3 bg-zinc-50 text-xs text-zinc-500 border-t border-zinc-200" data-testid="estate-overview-disclaimer">
        Indicative estimates from your calculator submission. Final values depend on formal administration and SARS assessment.
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Client portal — sanitized notice
// ---------------------------------------------------------------------------

function ClientRescueNotice({ summary }) {
  return (
    <div className="px-6 py-4 border-t border-zinc-200 bg-blue-50/40" data-testid="client-rescue-notice">
      <div className="flex items-start gap-3">
        <CheckCircle2 className="h-5 w-5 text-blue-700 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-medium text-zinc-900" data-testid="client-rescue-headline">
            We are working on resolving the estate shortfall
          </div>
          <div className="text-xs text-zinc-600 mt-1" data-testid="client-rescue-status">
            Plan: <strong className="text-zinc-900">{summary.selected_label}</strong> · Step {summary.completed_steps} of {summary.total_steps} complete ({summary.completion_pct}%).
          </div>
          <div className="h-1.5 bg-white rounded-full overflow-hidden mt-2 max-w-md">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${summary.completion_pct}%` }} />
          </div>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Admin — full tracker
// ---------------------------------------------------------------------------

function AdminRescueSection({ matter, tracker, rescueOptions, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const selectPath = async (selectedPath) => {
    setBusy(true);
    try {
      await api.put(`/admin/matters/${matter.id}/rescue-path`, { selected_path: selectedPath });
      toast.success("Rescue path selected");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const clearPath = async () => {
    if (!window.confirm("Clear the current rescue path and reset the checklist?")) return;
    setBusy(true);
    try {
      await api.delete(`/admin/matters/${matter.id}/rescue-path`);
      toast.success("Rescue path cleared");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const updateStep = async (stepId, body) => {
    setBusy(true);
    try {
      await api.patch(`/admin/matters/${matter.id}/rescue-path/steps/${stepId}`, body);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!tracker) {
    return (
      <div className="px-6 py-5 border-t border-zinc-200" data-testid="admin-rescue-selector">
        <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase mb-2">Select a rescue path</div>
        <div className="text-xs text-zinc-600 mb-3">Picking a path generates a structured checklist for tracking progress.</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {rescueOptions.map((opt) => (
            <button key={opt.id} type="button" disabled={busy || opt.applicable === false}
                       onClick={() => selectPath(opt.id)}
                       data-testid={`admin-select-rescue-${opt.id}`}
                       className={`text-left p-3 border transition-colors ${opt.applicable === false ? "border-zinc-200 opacity-50 cursor-not-allowed" : "border-zinc-300 hover:border-zinc-900 hover:shadow-sm"}`}>
              <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                {opt.title}
                <span className={`text-[10px] tracking-[0.14em] uppercase px-1.5 py-0.5 ${COMPLEXITY_STYLES[opt.complexity] || "bg-zinc-100"}`}>{opt.complexity}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-1">{opt.timing}</div>
              {opt.applicable === false && <div className="text-xs text-zinc-500 mt-1 italic">Not applicable to this estate</div>}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const completed = (tracker.steps || []).filter((s) => s.status === "complete").length;
  const total = (tracker.steps || []).length;
  const pct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="border-t border-zinc-200" data-testid="admin-rescue-tracker">
      <button type="button" onClick={() => setExpanded((e) => !e)}
                 className="w-full px-6 py-4 flex items-center justify-between hover:bg-zinc-50/60"
                 data-testid="admin-rescue-tracker-toggle">
        <div className="text-left">
          <div className="text-overline text-zinc-500 text-xs tracking-[0.18em] uppercase">Rescue path tracker</div>
          <div className="text-sm font-medium text-zinc-900 mt-0.5">
            {tracker.selected_label} · {completed}/{total} complete ({pct}%)
          </div>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
      </button>
      {expanded && (
        <div className="px-6 pb-5 space-y-3">
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${pct === 100 ? "bg-emerald-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
          </div>
          <ol className="space-y-2" data-testid="admin-rescue-steps">
            {(tracker.steps || []).map((step, idx) => (
              <RescueStepRow key={step.id} step={step} idx={idx} onUpdate={updateStep} busy={busy} />
            ))}
          </ol>
          <div className="flex flex-wrap gap-2 pt-2">
            <button onClick={() => onChanged?.()} disabled={busy}
                       className="text-xs text-zinc-600 hover:text-zinc-900 inline-flex items-center gap-1.5"
                       data-testid="admin-rescue-refresh-btn">
              <RefreshCw className="h-3 w-3" /> Refresh
            </button>
            <button onClick={clearPath} disabled={busy}
                       className="text-xs text-red-600 hover:text-red-800 inline-flex items-center gap-1.5"
                       data-testid="admin-rescue-clear-btn">
              <Trash2 className="h-3 w-3" /> Change rescue path
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function RescueStepRow({ step, idx, onUpdate, busy }) {
  const [draftStatus, setDraftStatus] = useState(step.status);
  const [draftNotes, setDraftNotes] = useState(step.notes || "");
  const [showNotes, setShowNotes] = useState(!!step.notes);
  const styles = STATUS_STYLES[step.status] || STATUS_STYLES.pending;
  const saveStatus = (newStatus) => {
    setDraftStatus(newStatus);
    onUpdate(step.id, { status: newStatus });
  };
  const saveNotes = () => {
    if (draftNotes === (step.notes || "")) return;
    onUpdate(step.id, { notes: draftNotes });
  };
  return (
    <li className="border border-zinc-200 p-3" data-testid={`admin-rescue-step-${step.id}`}>
      <div className="flex items-start gap-3">
        <span className={`flex-shrink-0 h-7 w-7 rounded-full ${styles.bg} ${styles.text} text-xs flex items-center justify-center font-medium`} title={step.status}>
          {styles.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-zinc-900">{idx + 1}. {step.label}</span>
            <select value={draftStatus} onChange={(e) => saveStatus(e.target.value)} disabled={busy}
                       data-testid={`admin-rescue-step-status-${step.id}`}
                       className="border border-zinc-300 px-2 py-1 text-xs bg-white">
              <option value="pending">Pending</option>
              <option value="in_progress">In progress</option>
              <option value="complete">Complete</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          {!showNotes && !step.notes && (
            <button onClick={() => setShowNotes(true)} className="mt-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid={`admin-rescue-step-notes-toggle-${step.id}`}>
              + Add note
            </button>
          )}
          {(showNotes || step.notes) && (
            <textarea
              value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} onBlur={saveNotes}
              placeholder="Optional notes…"
              rows={2}
              data-testid={`admin-rescue-step-notes-${step.id}`}
              className="w-full mt-2 border border-zinc-200 px-2 py-1 text-xs bg-white" />
          )}
        </div>
      </div>
    </li>
  );
}


function Cell({ label, value, testid }) {
  return (
    <div className="p-4 bg-white" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div className="mt-1 font-serif text-lg lg:text-xl text-zinc-900">{value}</div>
    </div>
  );
}
